import { Box, Typography, useTheme } from '@mui/material';
import { AssetType } from 'src/types/assets';
import { useGetLatestGatewayReadings } from 'src/hooks/useGetLatestGatewayReadings';
import { WET_WELL_TAG_NAME } from 'src/features/sites';

type Props = {
  asset: AssetType | undefined;
  waterLevel: number | null;
  unit: string;
  height: number;
};

export const WetWellWidget = ({ asset, waterLevel, height, unit }: Props) => {
  const theme = useTheme();

  const waterTowerAsset = asset?.assignedTags?.find(
    (tag) => tag?.tagName?.toLowerCase() === WET_WELL_TAG_NAME
  );

  const assignedTagId = waterTowerAsset?.assignedTagId;
  const gatewayId = waterTowerAsset?.assignedGatewayId;
  const { readings: latestReadings } = useGetLatestGatewayReadings({
    gatewayId: gatewayId,
    tagId: assignedTagId,
  });
  const readingsRawInputName = assignedTagId && latestReadings[assignedTagId];
  const readingsValues = readingsRawInputName?.values;
  const readingValueList = readingsValues && Object.values(readingsValues);

  const readingValue = readingValueList && readingValueList[0];
  const roundedWaterLevel = readingValue !== false ? Math.round(readingValue * 10) / 10 : -1;
  const waterLevelData =
    waterLevel !== null && waterLevel !== undefined ? waterLevel : Number(roundedWaterLevel);
  const validLevel = waterLevelData !== null && waterLevelData !== -1 && !isNaN(waterLevelData);

  const widthOfElement = 50;
  const heightOfElement = 90;
  const tankHeight = heightOfElement * 0.99;

  const waterHeightPx = ((waterLevelData ?? 0) / height!) * tankHeight;

  const labelBottom = Math.min(waterHeightPx, tankHeight - 58);

  const waterLevelPercentage = (waterLevelData! / height!) * 100;
  const safeWaterLevelData =
    waterLevelData !== null && waterLevelData !== undefined && !isNaN(waterLevelData)
      ? waterLevelData
      : undefined;

  return (
    <Box
      data-sm="asset-controller-wet-well-widget"
      sx={{
        position: 'relative',
        width: widthOfElement,
        height: heightOfElement,
        border: `0.1px solid ${theme.palette.grey[400]}`,
        background: '#f4f4f7',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: `${(heightOfElement * waterLevelPercentage) / 100}px`,
          backgroundColor: '#cbdef7',
          transition: 'height 0.3s ease',
        }}
      />

      {validLevel !== null && (
        <Typography
          variant="caption"
          data-sm="asset-controller-well-value"
          sx={{
            position: 'absolute',
            bottom: `${labelBottom}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            fontWeight: 500,
            fontSize: '10px',
            px: 0.5,
            borderRadius: 1,
            textAlign: 'center',
          }}
        >
          {safeWaterLevelData} {unit}
        </Typography>
      )}
    </Box>
  );
};
